import { lazy, ComponentProps } from 'react';

export type AssetsSubFolderType = 'dboo' | 'docTypes';
type IconProps = {
  name: string;
  subFolder?: AssetsSubFolderType;
  qaSelector?: string;
};

export type LazySvgProps = ComponentProps<'svg'> & IconProps;
type LazyImportProps = IconProps & {
  folder: string;
};

const subFolderMap = {
  dboo: 'dboo',
  docTypes: 'document-types',
};

/* Brands that opt into the brand-generic themable icon set.
   For these brands, assets/brand-generic/<name>.svg wins over the
   brand-specific folder. Other brands keep their existing
   brand-folder overrides untouched. */
export const BRAND_GENERIC_ENABLED = new Set([
  'privacy',
  'iddefense',
  'boapa',
  'afs2',
  'totalav',
  'ultraav',
  'dminor',
  'mshield',
  'hometitle',
  'enguard',
]);

export function lazyImport({ name, subFolder, folder }: LazyImportProps) {
  const lazyIcon = lazy(async () => {
    /* Lookup order (privacy / iddefense):
       1. brand-generic/         — themable, brand-agnostic; wins over brand folder
       2. assets/<brand>/        — brand-specific override
       3. common/images-new/     — themable common
       4. common/                — original common

       Other brands skip step 1 — they keep their existing brand-folder
       artwork untouched. */
    if (!subFolder && BRAND_GENERIC_ENABLED.has(folder)) {
      try {
        return await import(`../../assets/brand-generic/${name}.svg?react`);
      } catch {
        /* fall through */
      }
    }
    try {
      if (subFolder) {
        return await import(
          `../../assets/${folder}/${subFolderMap[subFolder]}/${name}.svg?react`
        );
      }
      return await import(`../../assets/${folder}/${name}.svg?react`);
    } catch (error) {
      if (subFolder) {
        return import(
          `../../assets/common/${subFolderMap[subFolder]}/${name}.svg?react`
        );
      }
      try {
        return await import(
          `../../assets/common/images-multicolor/${name}.svg?react`
        );
      } catch {
        return import(`../../assets/common/${name}.svg?react`);
      }
    }
  });

  return lazyIcon;
}
